import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
// Import the logo file
import logo from '../../assets/FCH_Logo_v2-removebg-preview_2.png';

export default function Topbar({ onMenuClick }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { B, isDarkMode, toggleDarkMode } = useTheme();

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Debounced search function
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      setSearching(true);
      try {
        const results = [];

        // Search residents
        const residentsRes = await api.get('/residents', {
          params: { search: searchTerm, limit: 5 }
        });
        results.push(...residentsRes.data.data.map(r => ({
          type: 'resident',
          label: r.name,
          subtitle: `Room ${r.room_number} · Age ${new Date().getFullYear() - new Date(r.date_of_birth).getFullYear()} · ${r.gender}`,
          path: `/residents?id=${r.id}`,
          icon: '👥',
          id: r.id
        })));

        // Search medications
        const medsRes = await api.get('/emar/medications', {
          params: { search: searchTerm, limit: 5 }
        });
        results.push(...medsRes.data.data.map(m => ({
          type: 'medication',
          label: `${m.name} ${m.dosage}`,
          subtitle: `${m.resident_name} · ${m.route} · ${m.frequency}`,
          path: '/emar',
          icon: '💊',
          id: m.id
        })));

        // Search staff
        const staffRes = await api.get('/team', {
          params: { search: searchTerm, limit: 5 }
        });
        results.push(...staffRes.data.data.map(s => ({
          type: 'staff',
          label: s.name,
          subtitle: `${s.role} · ${s.email}`,
          path: `/staff?id=${s.id}`,
          icon: '👤',
          id: s.id
        })));

        // Search care plans
        const plansRes = await api.get('/care-plans', {
          params: { search: searchTerm, limit: 3 }
        });
        results.push(...plansRes.data.data.map(p => ({
          type: 'care_plan',
          label: p.title,
          subtitle: `${p.resident_name} · ${p.plan_type?.toUpperCase()}`,
          path: '/careplans',
          icon: '📄',
          id: p.id
        })));

        // Search assessments
        const assessmentsRes = await api.get('/assessments', {
          params: { search: searchTerm, limit: 3 }
        });
        results.push(...assessmentsRes.data.data.map(a => ({
          type: 'assessment',
          label: a.title,
          subtitle: `${a.resident_name} · Due ${new Date(a.due_date).toLocaleDateString()}`,
          path: '/assessments',
          icon: '📋',
          id: a.id
        })));

        setSearchResults(results);
        setShowSearchResults(results.length > 0);
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const handleResultClick = (result) => {
    setShowSearchResults(false);
    setSearchTerm('');
    if (result.type === 'resident') {
      navigate(`/residents?id=${result.id}`);
    } else {
      navigate(result.path);
    }
  };

  const initials = user?.name?.split(' ').map(n => n[0]).join('') || 'FC';

  const handleLogout = () => {
    setShowDropdown(false);
    logout();
    navigate('/login');
  };

  const handleProfile = () => {
    setShowDropdown(false);
    navigate('/account');
  };

  const handleSettings = () => {
    setShowDropdown(false);
    navigate('/settings');
  };

  const handleLogoClick = () => {
    navigate('/dashboard');
  };

  // Group results by type
  const groupedResults = {
    resident: searchResults.filter(r => r.type === 'resident'),
    medication: searchResults.filter(r => r.type === 'medication'),
    staff: searchResults.filter(r => r.type === 'staff'),
    care_plan: searchResults.filter(r => r.type === 'care_plan'),
    assessment: searchResults.filter(r => r.type === 'assessment'),
  };

  const typeLabels = {
    resident: 'Residents',
    medication: 'Medications',
    staff: 'Staff',
    care_plan: 'Care Plans',
    assessment: 'Assessments'
  };

  const typeIcons = {
    resident: '👥',
    medication: '💊',
    staff: '👤',
    care_plan: '📄',
    assessment: '📋'
  };

  return (
    <header style={{
      background: B.headerBg,
      height: 54,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 16px',
      gap: 16,
      flexShrink: 0,
      boxShadow: isDarkMode ? '0 2px 12px rgba(0,0,0,0.3)' : '0 2px 12px rgba(25,73,132,0.2)',
      position: 'sticky',
      top: 0,
      zIndex: 100
    }}>
      {/* Left section - Mobile menu button and Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={onMenuClick}
          style={{
            display: isMobile ? 'flex' : 'none',
            background: 'none',
            border: 'none',
            color: B.headerText,
            fontSize: 20,
            cursor: 'pointer',
            padding: '4px',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >☰</button>

        <div
          onClick={handleLogoClick}
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        >
          {/* Replace the SVG logo with your actual logo image */}
          <img
            src={logo}
            alt="Forward Care Hub Logo"
            style={{
              height: 40,
              width: 'auto',
              display: 'block'
            }}
          />
          {/* Hide the text logo since we're using the image logo */}
          <span style={{
            fontSize: 14,
            fontWeight: 700,
            color: B.headerText,
            whiteSpace: 'nowrap',
            display: 'none' // Hide the text logo
          }}>
            Forward <span style={{ color: B.green }}>Care Hub</span>
          </span>
        </div>
      </div>

      {/* Rest of the component remains the same */}
      {/* Center section - Search bar with results dropdown */}
      {!isMobile && (
        <div style={{
          position: 'relative',
          flex: 1,
          maxWidth: 450,
          margin: '0 auto'
        }} ref={searchRef}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(255,255,255,0.08)',
            border: `1px solid ${showSearchResults ? B.green : 'rgba(255,255,255,0.12)'}`,
            borderRadius: 8,
            padding: '6px 14px',
            width: '100%',
            transition: 'border-color 0.2s'
          }}>
            <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)' }}>🔍</span>
            <input
              type="text"
              placeholder="Search residents, medications, staff..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
              style={{
                background: 'none',
                border: 'none',
                color: B.headerText,
                fontSize: 13,
                width: '100%',
                outline: 'none'
              }}
            />
            {searching && (
              <div style={{
                width: 16,
                height: 16,
                border: `2px solid ${B.green}`,
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }} />
            )}
            {searchTerm && !searching && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSearchResults([]);
                  setShowSearchResults(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Search Results Dropdown */}
          {showSearchResults && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: 8,
              background: B.cardBg,
              borderRadius: 12,
              boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.15)',
              maxHeight: 450,
              overflowY: 'auto',
              zIndex: 1000,
              border: `1px solid ${B.border}`
            }}>
              {searching ? (
                <div style={{ padding: 24, textAlign: 'center', color: B.muted }}>
                  <div>Searching...</div>
                </div>
              ) : searchResults.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: B.muted }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
                  <div>No results found for "{searchTerm}"</div>
                  <div style={{ fontSize: 11, marginTop: 4 }}>Try searching by name, room number, or keyword</div>
                </div>
              ) : (
                <>
                  {Object.entries(groupedResults).map(([type, items]) => {
                    if (items.length === 0) return null;
                    return (
                      <div key={type}>
                        <div style={{
                          padding: '8px 16px',
                          fontSize: 10,
                          fontWeight: 700,
                          color: B.muted,
                          textTransform: 'uppercase',
                          background: B.bg,
                          borderBottom: `1px solid ${B.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6
                        }}>
                          <span>{typeIcons[type]}</span>
                          <span>{typeLabels[type]}</span>
                          <span style={{ fontSize: 9, color: B.green }}>({items.length})</span>
                        </div>
                        {items.map((item, idx) => (
                          <button
                            key={`${type}-${idx}`}
                            onClick={() => handleResultClick(item)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              width: '100%',
                              padding: '10px 16px',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              textAlign: 'left',
                              transition: 'background 0.2s',
                              borderBottom: `1px solid ${B.border}40`
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = B.bg}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              background: `${B.green}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 16
                            }}>
                              {item.icon}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: B.navy }}>{item.label}</div>
                              <div style={{ fontSize: 11, color: B.muted, marginTop: 2 }}>{item.subtitle}</div>
                            </div>
                            <span style={{ fontSize: 12, color: B.muted }}>→</span>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                  <div style={{
                    padding: '8px 16px',
                    textAlign: 'center',
                    borderTop: `1px solid ${B.border}`,
                    fontSize: 11,
                    color: B.muted,
                    background: B.bg
                  }}>
                    Press Enter to see all results
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Right section - Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* Rest of your component remains unchanged */}
        <button
          onClick={toggleDarkMode}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: 20,
            padding: '6px 12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: B.headerText,
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
        >
          {isDarkMode ? (
            <>
              <span>☀️</span>
              <span style={{ fontSize: 11, display: isMobile ? 'none' : 'inline' }}>Light</span>
            </>
          ) : (
            <>
              <span>🌙</span>
              <span style={{ fontSize: 11, display: isMobile ? 'none' : 'inline' }}>Dark</span>
            </>
          )}
        </button>

        <div style={{ position: 'relative', cursor: 'pointer' }}>
          <span style={{ fontSize: '1.1rem' }}>🔔</span>
          <div style={{
            position: 'absolute',
            top: -4,
            right: -4,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: B.green,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 8,
            fontWeight: 700,
            color: '#fff'
          }}>3</div>
        </div>

        {/* User Dropdown */}
        <div style={{ position: 'relative' }} ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: 8,
              transition: 'background 0.2s',
              backgroundColor: showDropdown ? 'rgba(255,255,255,0.15)' : 'transparent'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
            onMouseLeave={(e) => {
              if (!showDropdown) e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <div style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: `linear-gradient(135deg,${B.green},${B.teal})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
              color: '#fff',
              flexShrink: 0
            }}>{initials}</div>
            {!isMobile && user && (
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: B.headerText }}>{user.name?.split(' ')[0]}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>{user.role?.replace('_', ' ')}</div>
              </div>
            )}
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>▼</span>
          </button>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: 8,
              background: B.cardBg,
              borderRadius: 12,
              boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.15)',
              minWidth: 200,
              overflow: 'hidden',
              zIndex: 1000,
              border: `1px solid ${B.border}`
            }}>
              <div style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${B.border}`,
                background: B.bg
              }}>
                <div style={{ fontWeight: 700, color: B.text, fontSize: 13 }}>{user?.name}</div>
                <div style={{ fontSize: 11, color: B.muted, marginTop: 2 }}>{user?.email}</div>
                <div style={{ fontSize: 10, color: B.teal, marginTop: 4, textTransform: 'capitalize' }}>{user?.role?.replace('_', ' ')}</div>
              </div>

              {isMobile && (
                <button
                  onClick={() => {
                    toggleDarkMode();
                    setShowDropdown(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: '10px 16px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: 12,
                    color: B.text,
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = B.bg}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{ fontSize: 14 }}>{isDarkMode ? '☀️' : '🌙'}</span>
                  <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                </button>
              )}

              <button
                onClick={handleProfile}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                  padding: '10px 16px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: 12,
                  color: B.text,
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = B.bg}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ fontSize: 14 }}>👤</span>
                <span>My Account</span>
              </button>

              {isAdmin && (
                <button
                  onClick={handleSettings}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: '10px 16px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: 12,
                    color: B.text,
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = B.bg}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{ fontSize: 14 }}>⚙️</span>
                  <span>Settings</span>
                </button>
              )}

              <div style={{ height: 1, background: B.border, margin: '4px 0' }} />

              <button
                onClick={handleLogout}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                  padding: '10px 16px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: 12,
                  color: '#dc2626',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ fontSize: 14 }}>🚪</span>
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add spinning animation to CSS */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </header>
  );
}